import { useClient } from '@cortex/react-query';
import type { MCPConnection } from '@cortex/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import {
  connectionOptionText,
  type ITestConnectionResult,
} from '../../mcp-connections/McpToolsEditor';

import type { INodeConfigProps } from './index';

interface IListEnvelope<T> {
  items: T[];
  total: number;
}

/**
 * Schema-aware editor for `mcp-query` nodes — picks an MCP connection and a
 * tool exposed by that connection. Mirrors `mcpQueryConfigSchema` in
 * packages/node-types/src/mcp-query/schema.ts. Tools come from the
 * connection's `toolSchemaCache`, populated by
 * `POST /v1/mcp-connections/:id/test-connection`.
 *
 * That cache is `null` on a connection nobody ever probed, and nothing about
 * picking a connection *here* populates it — so selecting a connection whose
 * cache is empty probes it automatically, instead of dead-ending the author
 * with "go test it on another page" (DEV-17129). The probe reuses the
 * `['mcp-tools', id]` query the `llm-agent` tool picker already runs against
 * the same endpoint, so react-query dedupes the two and a connection wired
 * into both node types is only probed once.
 *
 * Defensive paths: surface a fetch error inline rather than show an empty
 * dropdown, keep an already-saved `toolName` selectable even when the cache
 * is empty (so reopening a configured node never looks like it lost its
 * tool), and call out a saved `connectionRef` that no longer matches any
 * connection in the list (soft-deleted, different org, etc.) instead of
 * silently falling back to "Select a connection".
 */
export function McpQueryConfig({ config, onChange }: INodeConfigProps) {
  const client = useClient();
  const qc = useQueryClient();
  const connections = useQuery({
    queryKey: ['mcp-connections', 'for-node-config'],
    queryFn: () =>
      client.http.get('v1/mcp-connections?limit=200').json<IListEnvelope<MCPConnection>>(),
  });
  const connectionRef = typeof config.connectionRef === 'string' ? config.connectionRef : '';
  const toolName = typeof config.toolName === 'string' ? config.toolName : '';
  // Reliability + safety knobs from `mcpQueryConfigSchema`. The schema field
  // names are the canonical wire shape; we render them as labeled controls
  // here so a workflow author doesn't have to drop into JSON to pick
  // `unreachable` between fail / retry / skip.
  const timeoutMs = typeof config.timeoutMs === 'number' ? config.timeoutMs : '';
  const maxResponseBytes =
    typeof config.maxResponseBytes === 'number' ? config.maxResponseBytes : '';
  // `unreachable` defaults to "fail" at runtime (no retry) — surface that to
  // the author so the dropdown's resting state is what actually runs.
  const unreachable =
    config.unreachable === 'retry' || config.unreachable === 'fail' || config.unreachable === 'skip'
      ? config.unreachable
      : '';
  const toolError =
    config.toolError === 'report-to-agent' || config.toolError === 'fail-node'
      ? config.toolError
      : '';

  const items = connections.data?.items ?? [];
  const noConnections: boolean = connections.isSuccess && items.length === 0;
  const selected = items.find((c) => c.id === connectionRef || c.name === connectionRef) ?? null;
  // `staleRef` is true when the saved config points to a connection that has
  // since been deleted or otherwise dropped off this org's list. Distinct
  // from "user hasn't picked one yet" (connectionRef === '').
  const staleRef = connectionRef !== '' && selected === null && connections.isSuccess;
  const cachedTools = selected ? listTools(selected) : [];
  /** OAuth connections can't be probed until this org has stored credentials. */
  const canProbe = selected !== null && (selected.authType !== 'oauth' || selected.hasKeyring);

  // Auto-probe: a selected connection with an empty cache gets its tools
  // fetched without the author having to ask. Keyed by connection id, so the
  // result can never bleed across a connection switch and it shares the
  // `llm-agent` picker's cache entry. `retry: false` keeps an unreachable
  // server to one round-trip (the manual retry below is the escape hatch);
  // `staleTime` stops a re-probe every time the inspector is reopened.
  const probe = useQuery({
    enabled: selected !== null && canProbe && cachedTools.length === 0,
    queryKey: ['mcp-tools', selected?.id],
    queryFn: () =>
      client.http
        .post(`v1/mcp-connections/${selected?.id}/test-connection`)
        .json<ITestConnectionResult>(),
    retry: false,
    staleTime: 5 * 60_000,
  });

  // A successful probe rewrote `toolSchemaCache` server-side — refresh the
  // connection lists so every other tool picker sees it too. Once per
  // connection: the invalidation refetches the list this component reads, and
  // re-invalidating on each resulting render would loop.
  const invalidatedFor = useRef(new Set<string>());
  const probedId = probe.data?.ok === true ? (selected?.id ?? null) : null;
  useEffect(() => {
    if (probedId === null || invalidatedFor.current.has(probedId)) return;
    invalidatedFor.current.add(probedId);
    qc.invalidateQueries({ queryKey: ['mcp-connections'] }).catch(() => undefined);
  }, [probedId, qc]);

  const tools = selected
    ? toolOptions({
        cached: cachedTools,
        probed: probe.data?.ok === true ? probe.data.tools.map((t) => t.name) : [],
        saved: toolName,
      })
    : [];
  /**
   * Probe outcomes worth showing: a transport-level failure (`ok: false`
   * carries the remote's error) and a fetch that never landed (network / 4xx
   * on our own API, where `probe.error` is the thrown ky error).
   */
  const probeError =
    probe.data?.ok === false
      ? (probe.data.error?.message ?? 'Connection test failed')
      : probe.error
        ? (probe.error as Error).message
        : null;
  const needsTest = selected !== null && tools.length === 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
      {connections.error ? (
        <div className="alert alert--danger" style={{ fontSize: 12 }}>
          Failed to load MCP connections: {(connections.error as Error).message}
        </div>
      ) : null}

      <label className="field">
        <span className="field-label">MCP connection</span>
        <select
          className="select-native"
          value={connectionRef}
          onChange={(e) => onChange({ connectionRef: e.target.value, toolName: '' })}
          disabled={connections.isLoading || noConnections}
        >
          <option value="">— Select a connection —</option>
          {items.map((c) => {
            // Shared with the `llm-agent` picker so both dropdowns read the
            // same — status badge, name, description (DEV-17129).
            const opt = connectionOptionText(c);
            return (
              <option key={c.id} value={c.id} title={opt.title}>
                {opt.label}
              </option>
            );
          })}
        </select>
        {noConnections ? (
          <span className="field-help" style={{ color: 'var(--status-warning)' }}>
            No MCP connections in this org yet — create one in <code>/mcp-connections</code> first.
          </span>
        ) : staleRef ? (
          <span className="field-help" style={{ color: 'var(--status-warning)' }}>
            Saved connection <code>{connectionRef}</code> is no longer available — pick a
            replacement or recreate the connection in <code>/mcp-connections</code>.
          </span>
        ) : (
          <span className="field-help">
            Runtime resolves the connection by id; tools below come from its cached schema.
          </span>
        )}
      </label>

      <label className="field">
        <span className="field-label">Tool</span>
        <select
          className="select-native"
          value={toolName}
          onChange={(e) => onChange({ toolName: e.target.value })}
          disabled={!selected || tools.length === 0}
        >
          <option value="">{selected ? '— Select a tool —' : '— Pick a connection first —'}</option>
          {tools.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        {needsTest ? (
          !canProbe ? (
            <span className="field-help" style={{ color: 'var(--status-warning)' }}>
              No tools cached for this connection. Authenticate it in <code>/mcp-connections</code>{' '}
              first — an OAuth connection can&apos;t be probed without stored credentials.
            </span>
          ) : probe.isFetching ? (
            <span className="field-help">Loading tools from the MCP server…</span>
          ) : probeError ? (
            <span className="field-help" style={{ color: 'var(--status-danger)' }}>
              Couldn&apos;t load tools: {probeError}{' '}
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => {
                  probe.refetch().catch(() => undefined);
                }}
              >
                Retry
              </button>
            </span>
          ) : (
            <span className="field-help" style={{ color: 'var(--status-warning)' }}>
              This connection advertises no tools.
            </span>
          )
        ) : null}
      </label>

      <label className="field">
        <span className="field-label">If MCP server is unreachable</span>
        <select
          className="select-native"
          value={unreachable}
          onChange={(e) => onChange({ unreachable: e.target.value || undefined })}
        >
          <option value="">— Runtime default (fail) —</option>
          <option value="fail">Fail the node</option>
          <option value="retry">Retry the call (recommended for safety-critical fetches)</option>
          <option value="skip">Skip this node and continue downstream</option>
        </select>
        <span className="field-help">
          Drives `mcp-query`&apos;s transport-error policy. `retry` re-establishes the connection on
          transient transport failures (most common cause of an otherwise-completed run failing).
          `skip` lets downstream nodes proceed without this call&apos;s output — only use for
          enrichment, not safety-critical context.
        </span>
      </label>

      <label className="field">
        <span className="field-label">If the tool itself returns an error</span>
        <select
          className="select-native"
          value={toolError}
          onChange={(e) => onChange({ toolError: e.target.value || undefined })}
        >
          <option value="">— Runtime default (report-to-agent) —</option>
          <option value="report-to-agent">Report the error in the node output</option>
          <option value="fail-node">Fail the node</option>
        </select>
        <span className="field-help">
          `report-to-agent` keeps the run alive and lets a downstream LLM decide how to handle a
          tool error. `fail-node` short-circuits the run.
        </span>
      </label>

      <label className="field">
        <span className="field-label">Timeout (ms)</span>
        <input
          className="input"
          type="number"
          min={1}
          step={1000}
          value={timeoutMs}
          onChange={(e) => {
            const v = e.target.value === '' ? undefined : Number(e.target.value);
            onChange({ timeoutMs: Number.isFinite(v) && (v as number) > 0 ? v : undefined });
          }}
          placeholder="connection default"
        />
        <span className="field-help">
          Per-call timeout. Leave blank to use the connection&apos;s default.
        </span>
      </label>

      <label className="field">
        <span className="field-label">Max response size (bytes)</span>
        <input
          className="input"
          type="number"
          min={1}
          step={1024}
          value={maxResponseBytes}
          onChange={(e) => {
            const v = e.target.value === '' ? undefined : Number(e.target.value);
            onChange({ maxResponseBytes: Number.isFinite(v) && (v as number) > 0 ? v : undefined });
          }}
          placeholder="no cap"
        />
        <span className="field-help">
          Reject responses larger than this. Prevents a runaway tool result from blowing past
          downstream prompt-token caps. Leave blank for no cap.
        </span>
      </label>
    </div>
  );
}

/**
 * Pure helper: pull the tool names out of an `MCPConnection.toolSchemaCache`.
 * Returns a sorted list so the dropdown order is stable across renders.
 * Defensive against a `null` cache (a freshly-created connection has `null`
 * until someone runs the test endpoint) and against shape drift on the
 * underlying JSON column.
 */
export function listTools(connection: {
  toolSchemaCache: MCPConnection['toolSchemaCache'];
}): string[] {
  const cache = connection.toolSchemaCache;
  if (!cache) return [];
  return Object.keys(cache.schemas).sort();
}

/**
 * Pure helper: the tool names the dropdown should offer.
 *
 * `cached` (the connection's persisted `toolSchemaCache`) wins when present;
 * `probed` is the just-fetched list from an on-demand `test-connection`,
 * which lets the author pick a tool immediately instead of waiting for the
 * invalidated connection list to come back. A non-empty `saved` is always
 * included so an already-configured node keeps showing its tool even when
 * neither source has it — the runtime only requires a non-empty string
 * (`mcpQueryConfigSchema`), so dropping it from the options would misreport
 * a perfectly valid config as unset.
 */
export function toolOptions({
  cached,
  probed,
  saved,
}: {
  cached: string[];
  probed: string[];
  saved: string;
}): string[] {
  const names = new Set(cached.length > 0 ? cached : probed);
  if (saved !== '') names.add(saved);
  return [...names].sort();
}
